import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * Standardized form text field with built-in validation display
 *
 * @param {Object} props - Component props
 * @param {string} props.name - Field name (used for error identification)
 * @param {string} props.label - Field label
 * @param {string} props.value - Field value
 * @param {Function} props.onChange - Change handler
 * @param {Function} props.onBlur - Blur handler (for validation)
 * @param {string} props.error - Error message (translation key or text)
 * @param {string} props.type - Input type (default: 'text')
 * @param {boolean} props.required - Whether field is required
 * @param {boolean} props.disabled - Whether field is disabled
 * @param {boolean} props.multiline - Whether field is multiline
 * @param {number} props.rows - Number of rows for multiline
 * @param {Object} props.slotProps - MUI slotProps for customization
 * @param {Object} props.sx - Additional styles
 */
const FormTextField = ({
  name,
  label,
  value,
  onChange,
  onBlur,
  error,
  type = 'text',
  required = false,
  disabled = false,
  multiline = false,
  rows = 1,
  slotProps,
  sx,
  ...rest
}) => {
  const { t } = useTranslation();

  // Handle error display - can be a translation key or direct text
  const getErrorText = () => {
    if (!error) return '';

    // If it's an object with key and params (from validation hook)
    if (typeof error === 'object' && error.key) {
      return t(error.key, error.params);
    }

    // If it starts with 'validation.' or 'errors.', translate it
    if (typeof error === 'string' && (error.startsWith('validation.') || error.startsWith('errors.'))) {
      return t(error);
    }

    // Otherwise, return as-is
    return error;
  };

  return (
    <TextField
      name={name}
      label={label}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      type={type}
      required={required}
      disabled={disabled}
      multiline={multiline}
      rows={multiline ? rows : undefined}
      fullWidth
      margin="normal"
      error={!!error}
      helperText={getErrorText()}
      slotProps={slotProps}
      sx={sx}
      {...rest}
    />
  );
};

export default FormTextField;
